/**
 * The owner's month, across every company at once.
 *
 * This is the one screen that deliberately spans tenants. A company admin
 * opening it sees only their own company — the API decides that from the
 * caller's role, so there is no company selector here to get out of step
 * with what the caller is actually allowed to see.
 *
 * There is no profit column, and that is deliberate: see the note below the
 * table. Placement billing and payroll cost are both shown, and the reader
 * holds them side by side themselves.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Building2 } from 'lucide-react';
import { client } from '../../api/client';
import { money } from '../../lib/money';
import type { Overview, PayrollRunStatus } from '../../types';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const STATUS_TONE: Record<PayrollRunStatus, string> = {
  draft: 'bg-gray-100 text-gray-600',
  calculated: 'bg-amber-100 text-amber-700',
  approved: 'bg-emerald-100 text-emerald-700',
  paid: 'bg-blue-100 text-blue-700',
  cancelled: 'bg-red-100 text-red-700',
};

/** The month before the current one — the last period with a full month of data. */
function lastCompleteMonth(): { year: number; month: number } {
  const now = new Date();
  const month = now.getMonth(); // 0-indexed, so this is already one month back
  return month === 0
    ? { year: now.getFullYear() - 1, month: 12 }
    : { year: now.getFullYear(), month };
}

export default function OverviewPage() {
  const initial = lastCompleteMonth();
  const [year, setYear] = useState(initial.year);
  const [month, setMonth] = useState(initial.month);

  const overview = useQuery<Overview>({
    queryKey: ['overview', year, month],
    queryFn: async () =>
      (
        await client.get('/api/v1/admin/overview', {
          params: { year, month },
          // The point of this screen is the comparison across companies.
          // Narrowing it to the one the owner is acting as would leave a
          // cross-company dashboard showing a single row.
          skipActingCompany: true,
        })
      ).data,
  });

  const rows = overview.data?.companies ?? [];
  const totals = overview.data?.totals;

  return (
    <div className="space-y-6">
      <section className="card">
        <h2 className="text-sm font-semibold text-gray-900">Overview</h2>
        <p className="mt-1 text-xs text-gray-500">
          Every figure below is bounded to the month selected here.
        </p>

        <div className="mt-3 flex flex-wrap items-end gap-3">
          <label className="text-xs font-medium text-gray-600">
            Month
            <select
              className="input mt-1 block"
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
            >
              {MONTHS.map((label, index) => (
                <option key={label} value={index + 1}>{label}</option>
              ))}
            </select>
          </label>

          <label className="text-xs font-medium text-gray-600">
            Year
            <input
              type="number"
              className="input mt-1 block w-28"
              value={year}
              min={2000}
              max={2100}
              onChange={(e) => setYear(Number(e.target.value))}
            />
          </label>
        </div>
      </section>

      {overview.isLoading ? (
        <div className="flex justify-center py-10 text-gray-400">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : overview.isError ? (
        <div className="card flex items-center justify-between border border-red-200 bg-red-50">
          <p className="text-sm text-red-800">
            Could not load the overview. This is a failure to fetch, not an
            empty month.
          </p>
          <button
            type="button"
            className="btn-ghost"
            onClick={() => void overview.refetch()}
          >
            Retry
          </button>
        </div>
      ) : rows.length === 0 ? (
        <div className="card text-center">
          <Building2 className="mx-auto h-6 w-6 text-gray-300" />
          <p className="mt-2 text-sm text-gray-600">No companies to report on.</p>
        </div>
      ) : (
        <>
          {totals && (
            <section className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
              <Tile label="Companies" value={String(rows.length)} />
              <Tile label="On payroll" value={String(totals.activeEmployeeCount)} />
              <Tile label="Calls" value={String(totals.calls)} />
              <Tile label="Submissions" value={String(totals.submissions)} />
              <Tile label="Placements" value={String(totals.placements)} />
              <Tile label="Placement billing" value={money(totals.placementBilling)} />
            </section>
          )}

          <section>
            <h2 className="mb-2 text-sm font-semibold text-gray-900">
              {MONTHS[month - 1]} {year} by company
            </h2>

            <div className="overflow-x-auto rounded-xl bg-white ring-1 ring-gray-200">
              <table className="w-full text-sm">
                <thead className="border-b border-gray-200 bg-gray-50 text-left text-xs text-gray-500">
                  <tr>
                    <th scope="col" className="px-4 py-2.5 font-medium">Company</th>
                    <th scope="col" className="px-4 py-2.5 font-medium">On Payroll</th>
                    <th scope="col" className="px-4 py-2.5 font-medium">Calls</th>
                    <th scope="col" className="px-4 py-2.5 font-medium">Subs</th>
                    <th scope="col" className="px-4 py-2.5 font-medium">Placed</th>
                    <th scope="col" className="px-4 py-2.5 font-medium">Placement Billing</th>
                    <th scope="col" className="px-4 py-2.5 font-medium">Payroll Net</th>
                    <th scope="col" className="px-4 py-2.5 font-medium">Employer Cost</th>
                    <th scope="col" className="px-4 py-2.5 font-medium">Payroll</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.map((row) => (
                    <tr key={row.companyId} className="hover:bg-gray-50">
                      <td className="whitespace-nowrap px-4 py-2.5">
                        <span className="text-gray-900">{row.companyName}</span>
                        {row.city && (
                          <span className="ml-1.5 text-xs text-gray-400">{row.city}</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 tabular-nums text-gray-600">
                        {row.activeEmployeeCount}
                      </td>
                      <td className="px-4 py-2.5 tabular-nums text-gray-600">{row.calls}</td>
                      <td className="px-4 py-2.5 tabular-nums text-gray-600">
                        {row.submissions}
                      </td>
                      <td className="px-4 py-2.5 tabular-nums text-gray-600">
                        {row.placements}
                      </td>
                      <td className="px-4 py-2.5 tabular-nums text-gray-900">
                        {money(row.placementBilling)}
                      </td>
                      <td className="px-4 py-2.5 tabular-nums text-gray-600">
                        {money(row.payrollNet)}
                      </td>
                      <td
                        className="px-4 py-2.5 tabular-nums text-gray-500"
                        title="Employer contributions — cost to company, not deducted from anyone's pay"
                      >
                        {money(row.payrollEmployerCost)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5">
                        {row.payrollStatus ? (
                          <span className={`badge ${STATUS_TONE[row.payrollStatus]}`}>
                            {row.payrollStatus}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">not run</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                {totals && rows.length > 1 && (
                  <tfoot className="border-t-2 border-gray-200 bg-gray-50 font-medium">
                    <tr>
                      <td className="px-4 py-2.5 text-gray-900">Total</td>
                      <td className="px-4 py-2.5 tabular-nums text-gray-900">
                        {totals.activeEmployeeCount}
                      </td>
                      <td className="px-4 py-2.5 tabular-nums text-gray-900">
                        {totals.calls}
                      </td>
                      <td className="px-4 py-2.5 tabular-nums text-gray-900">
                        {totals.submissions}
                      </td>
                      <td className="px-4 py-2.5 tabular-nums text-gray-900">
                        {totals.placements}
                      </td>
                      <td className="px-4 py-2.5 tabular-nums text-gray-900">
                        {money(totals.placementBilling)}
                      </td>
                      <td className="px-4 py-2.5 tabular-nums text-gray-900">
                        {money(totals.payrollNet)}
                      </td>
                      <td className="px-4 py-2.5 tabular-nums text-gray-900">
                        {money(totals.payrollEmployerCost)}
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>

            <p className="mt-2 text-xs text-gray-500">
              Placement billing is what was invoiced for candidates who joined
              this month; payroll is what our own staff cost. They are not set
              against each other here on purpose — a one-off placement fee and
              a recurring wage bill are not comparable, and neither figure
              accounts for the rest of what a company spends.
            </p>
          </section>
        </>
      )}
    </div>
  );
}

function Tile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl bg-white p-3 ring-1 ring-gray-200">
      <p className="text-xs text-gray-500">{label}</p>
      <p className="mt-0.5 text-lg font-semibold tabular-nums text-gray-900">{value}</p>
    </div>
  );
}
